import { lazy, Suspense, Component, useEffect } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from './store'
import Layout from './components/Layout'
import { onForceLogout } from './api'

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null } }
  static getDerivedStateFromError(error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-4 text-center px-6">
          <div className="text-3xl">⚠️</div>
          <p className="text-sm text-[#e8e8f0] font-medium">Erro ao carregar esta página</p>
          <p className="text-xs text-[#6b6b80]">{this.state.error?.message || 'Erro desconhecido'}</p>
          <button
            onClick={() => this.setState({ error: null })}
            className="px-4 py-2 rounded-lg bg-[#6c63ff] text-white text-xs hover:bg-[#5a52d5] transition-colors"
          >
            Tentar novamente
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

const Login = lazy(() => import('./pages/Login'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Sales = lazy(() => import('./pages/Sales'))
const Clients = lazy(() => import('./pages/Clients'))
const CRM = lazy(() => import('./pages/CRM'))
const WhatsApp = lazy(() => import('./pages/WhatsApp'))
const Financial = lazy(() => import('./pages/Financial'))
const Stock = lazy(() => import('./pages/Stock'))
const NPS = lazy(() => import('./pages/NPS'))
const FollowUp = lazy(() => import('./pages/FollowUp'))
const Relationship = lazy(() => import('./pages/Relationship'))
const Goals = lazy(() => import('./pages/Goals'))
const Ranking = lazy(() => import('./pages/Ranking'))
const Loyalty = lazy(() => import('./pages/Loyalty'))
const Tasks = lazy(() => import('./pages/Tasks'))
const Exchanges = lazy(() => import('./pages/Exchanges'))
const Reports = lazy(() => import('./pages/Reports'))
const Ads = lazy(() => import('./pages/Ads'))
const SellerMode = lazy(() => import('./pages/SellerMode'))
const WhatsAppConnect = lazy(() => import('./pages/WhatsAppConnect'))
const Settings = lazy(() => import('./pages/Settings'))
const NPSForm = lazy(() => import('./pages/NPSForm'))
const ChangePassword = lazy(() => import('./pages/ChangePassword'))

function ProtectedRoute({ children, roles }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />
  return children
}

const darkFallback = <div className="min-h-screen bg-[#0a0a0f]" />

export default function App() {
  const { user } = useAuthStore()
  const navigate = useNavigate()

  // Escuta logout forçado pelo interceptor e navega SPA (sem reload de página)
  useEffect(() => {
    return onForceLogout(() => navigate('/login', { replace: true }))
  }, [navigate])

  // Suspense apenas para Login/NPSForm (fora do Layout que tem seu próprio Suspense)
  if (user?.role === 'seller') {
    return (
      <Routes>
        <Route path="/login" element={<Suspense fallback={darkFallback}><Login /></Suspense>} />
        <Route path="/nps/:token" element={<Suspense fallback={darkFallback}><NPSForm /></Suspense>} />
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route index element={<SellerMode />} />
          <Route path="/seller" element={<SellerMode />} />
          <Route path="/clients" element={<Clients />} />
          <Route path="/tasks" element={<Tasks />} />
          <Route path="/ranking" element={<Ranking />} />
          <Route path="/change-password" element={<ChangePassword />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={<Suspense fallback={darkFallback}><Login /></Suspense>} />
      <Route path="/nps/:token" element={<Suspense fallback={darkFallback}><NPSForm /></Suspense>} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="/sales" element={<Sales />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/crm" element={<CRM />} />
        <Route path="/whatsapp" element={<WhatsApp />} />
        <Route path="/followup" element={<FollowUp />} />
        <Route path="/relationship" element={<Relationship />} />
        <Route path="/nps-dashboard" element={<NPS />} />
        <Route path="/financial" element={<ProtectedRoute roles={['admin', 'manager']}><Financial /></ProtectedRoute>} />
        <Route path="/stock" element={<Stock />} />
        <Route path="/exchanges" element={<Exchanges />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/ranking" element={<Ranking />} />
        <Route path="/loyalty" element={<Loyalty />} />
        <Route path="/reports" element={<ProtectedRoute roles={['admin', 'manager']}><Reports /></ProtectedRoute>} />
        <Route path="/ads" element={<ProtectedRoute roles={['admin', 'manager']}><Ads /></ProtectedRoute>} />
        <Route path="/seller" element={<SellerMode />} />
        <Route path="/whatsapp-connect" element={<ProtectedRoute roles={['admin']}><WhatsAppConnect /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute roles={['admin']}><Settings /></ProtectedRoute>} />
        <Route path="/change-password" element={<ChangePassword />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
